import {identity, isUndefined, omit, pick} from 'lodash';
import memoize from 'memoize-one';
import {compose} from 'redux';
import uuid from 'uuid-random';

import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createResourceTableValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {leaveBreadcrumb, reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {htmlToText} from '../../../../srcCommon/services/converters/htmlToTextConverter';
import {textToHtml} from '../../../../srcCommon/services/converters/textToHtmlConverter';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {OfflineError} from '../../../../srcCommon/services/offline/offlineError';
import {AppTask} from '../../../../srcCommon/tasks/appTask';
import {RSendMessageTask} from '../../../../srcCommon/tasks/sendMessageTask';
import {createTask} from '../../../../srcCommon/tasks/task';
import {scheduleOfflineTaskAsyncAction} from '../../../../srcCommon/tasks/taskActions';
import {createCountersUpdaterForConversations} from '../../../helpers/counterHelpers';
import {
  autoAssignConversation,
  autoSubscribeToConversation,
  buildConversationUpdater,
  bumpConversationOnSend,
  bumpConversationTracker,
  ConversationStatusesEnum,
  findConversationStatus,
  isConversationInTeamContext,
  setConversationReminder,
  setConversationStatus,
  shouldAutoAssignChannel,
  shouldAutoAssignConversation,
} from '../../../helpers/models/conversationHelpers';
import {
  findDraftMessageData,
  findDraftValidationErrors,
  isDirtyDraftMessage,
  isDraftValid,
} from '../../../helpers/models/messageHelpers';
import {buildSignatureHtml} from '../../../helpers/models/signatureHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {BumpStrategiesEnum} from '../../../models/api/companyPreferencesModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {ConversationTrackerStatusesEnum} from '../../../models/api/conversationTrackerModel';
import {DataGroupModel} from '../../../models/api/dataGroupModel';
import {MessageErrorsEnum, MessageErrorSourcesEnum} from '../../../models/api/messageErrorModel';
import {
  DraftMessageModel,
  MessageStatusesEnum,
  MessageTypesEnum,
  MessageVisualStatusesEnum,
  SendActionsEnum,
  SendMessageOptionsModel,
} from '../../../models/api/messageModel';
import {reserveHandleTimeToSave, resetConversationTimer} from '../../../services/handleTime';
import {
  HttpError,
  HttpNetworkError,
  HttpResponseError,
  HttpTimeoutError,
} from '../../../services/http/httpError';
import {findConversationLock} from '../../../services/locks/resourceLocks';
import {findMessageQueue} from '../../../services/tasks/specializedTaskQueues';
import {AppState} from '../../../store/appState';
import {Dispatch, StateGetter} from '../../../store/appStore';
import {AppThunkDispatch, ThunkAsyncAction} from '../../../store/appThunk';
import {getCompany} from '../../../store/data/company/companySelectors';
import {updateClientCountersForConversationsAction} from '../../../store/data/counters/countersActions';
import {updateDataAction} from '../../../store/data/dataGroupsActions';
import {getConversations, getOptimisticData} from '../../../store/data/dataSelectors';
import {DataState} from '../../../store/data/dataState';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {getHasOnlineClientCounters} from '../../../store/featureFlags/featureFlagsSelectors';
import {
  finishOptimisticUpdateAction,
  startOptimisticUpdateAction,
} from '../../../store/optimisticUpdates/optimisticUpdatesActions';
import {OptimisticUpdater} from '../../../store/optimisticUpdates/optimisticUpdatesState';
import {showMessageUndoBannerAction} from '../../../store/panels/app/banners/bannersActions';
import {MessageUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {getOptionalFirstAppPanelId} from '../../panels/panelsSelectors';
import {
  clearHiddenConversationIdAction,
  maybeHideConversationAction,
} from '../conversations/conversationsActions';
import {
  maybeAddMessageVersionSetterToOptions,
  MessageVersionOptions,
  sendMessageApiAsync,
} from './messagesHelpers';
import {flushMessageAsyncAction} from './saveMessageActions';

/*
 * Constants.
 */

const handleTimeLogger = logger.getLogger('handleTime');

const sendOfflineUndoDelay = 8000;
const optimisticSendDelay = 10000;

/*
 * Action creators.
 */

export interface SendMessageOptions extends MessageVersionOptions {}

export function sendMessageAsyncAction(
  messageUid: string,
  sendOptions: SendMessageOptionsModel,
  options: SendMessageOptions,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const hasOnlineClientCounters = getHasOnlineClientCounters(getState());

    // Create an offline task as a fallback.
    const offlineTask = maybeCreateOfflineTask(getState, messageUid, sendOptions);
    const actionId = offlineTask ? offlineTask.id : uuid();

    // Start an optimistic update.
    const optimisticUpdater = createOptimisticUpdater(getState, messageUid, sendOptions);
    dispatch(startOptimisticUpdateAction(actionId, optimisticUpdater));

    try {
      maybeDispatchConversationSwitchingActions(dispatch, getState, sendOptions, messageUid);

      const resultDataGroup = await sendMessageAsync(
        dispatch,
        getState,
        messageUid,
        sendOptions,
        options,
        offlineTask,
      );

      // If we sent offline, we want to keep the optimistic update.
      if (!resultDataGroup) {
        return;
      }

      const finishOptimisticUpdate = () => {
        const resultAction = updateDataAction(resultDataGroup);
        dispatch(finishOptimisticUpdateAction(actionId, resultAction));
      };

      // Update client-side counters until we get a counters pusher update.
      if (hasOnlineClientCounters && resultDataGroup.conversations) {
        dispatch(
          updateClientCountersForConversationsAction(
            resultDataGroup.conversations.map((c) => c.id),
            finishOptimisticUpdate,
          ),
        );
        return;
      }

      // Finish the optimistic update.
      finishOptimisticUpdate();
    } catch (error) {
      // Don't leave the optimistic update hanging around.
      dispatch(finishOptimisticUpdateAction(actionId));

      throw error;
    }
  };
}

/*
 * Helpers.
 */

function maybeCreateOfflineTask(getState: StateGetter, messageUid: string, options: SendMessageOptionsModel) {
  // Use the optimistic state for two reasons:
  // - we need to bake some optimistic state into the offline task (unfortunately),
  // - the optimistic state may contain offline IDs from other pending offline tasks.
  const state = getState();
  const optimisticData = getOptimisticData(state);
  const {message, channel, conversation} = findDraftMessageData(optimisticData, messageUid);
  if (!message || !channel) {
    return undefined;
  }

  const userId = getUserId(state);
  const {cellsContext, sendAction, remindAt} = options;
  const {teammateId} = cellsContext;
  const shouldArchive = sendAction === SendActionsEnum.SEND_AND_ARCHIVE;
  const hasStatusUpdates = Boolean(shouldArchive || remindAt);

  // Skip send & archive/snooze in assignee and team contexts.
  const {shouldUpdateGlobal} = computeSendMessageContext(userId, channel, conversation, cellsContext);
  if (hasStatusUpdates && shouldUpdateGlobal) {
    return undefined;
  }

  // Skip if we have an existing conversation and the user is not already tracker.
  const tracker = conversation && conversation.trackers[teammateId];
  if (conversation && !tracker) {
    return undefined;
  }

  return createTask(RSendMessageTask, {
    initiatedAt: Date.now(),
    messageUid,
    sendAt: findSendAt(channel, options),
    optimisticContent: buildOptimisticContent(state, message),
    teammateId,
    lastBumpedAt: tracker && tracker.bumpedAt,
    offlineBumpedId: (tracker && tracker.offlineBumpedId) || uuid(),
    status: shouldArchive ? ConversationTrackerStatusesEnum.ARCHIVED : undefined,
    remindAt,
    tagIds: options.tagIds || [],
  });
}

function createOptimisticUpdater(
  getState: StateGetter,
  messageUid: string,
  options: SendMessageOptionsModel,
) {
  // TODO: Instead of baking the current optimistic state into the updater, use the data state passed into the
  // updater each time it recalculates.
  const state = getState();
  const optimisticData = getOptimisticData(state);
  const {message: optimisticMessage, channel: optimisticChannel} = findDraftMessageData(
    optimisticData,
    messageUid,
  );
  if (!optimisticMessage || !optimisticChannel) {
    return identity;
  }

  // Create the optimistic updater for the message.
  const content = buildOptimisticContent(state, optimisticMessage);
  const status = options.sendLaterAt ? MessageStatusesEnum.DRAFT_LATER : MessageStatusesEnum.DRAFT_SENDING;
  const sendAt = findSendAt(optimisticChannel, options);
  const optimisticSentAt = sendAt + optimisticSendDelay;
  const optimisticUpdaterForMessage = createObjectPropertyUpdater<DataState, 'messages'>(
    'messages',
    createResourceMapValueUpdater(
      messageUid,
      createUpdater((message) => {
        if (message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
          return message;
        }

        return {
          ...message,
          ...content,
          status,
          sentAt: optimisticSentAt,
        };
      }),
    ),
  );

  const hasOnlineClientCounters = getHasOnlineClientCounters(state);
  const createOptimisticUpdaterWithData = memoize(
    (
      bumpStrategy: BumpStrategiesEnum,
      shouldAutoAssign: boolean,
      userId: number,
      shouldAutoSubscribe: boolean,
      conversationId: number,
    ) => {
      // If the message does not yet have a conversation, there's nothing else we need to do.
      if (!conversationId) {
        return optimisticUpdaterForMessage;
      }

      // Create an optimistic updater for the conversation.
      const optimisticUpdaterForConversationBase = createObjectPropertyUpdater<DataState, 'conversations'>(
        'conversations',
        createResourceTableValueUpdater(
          conversationId,
          createUpdater(
            sendMessageInConversation(bumpStrategy, shouldAutoAssign, userId, shouldAutoSubscribe, options),
          ),
        ),
      );

      // Wrap for optimistic counter updates.
      const optimisticUpdaterForConversation = hasOnlineClientCounters
        ? createCountersUpdaterForConversations([conversationId], optimisticUpdaterForConversationBase)
        : optimisticUpdaterForConversationBase;

      // Compose the optimistic updaters.
      return compose(optimisticUpdaterForMessage, optimisticUpdaterForConversation);
    },
  );

  // This is a higher order optimistic updater because we depend on company, channel, and user preferences and we don't
  // want to break memoization on the entire data state.
  const optimisticUpdater: OptimisticUpdater = (data) => {
    const {message, channel} = findDraftMessageData(data, messageUid);
    if (!message || !channel) {
      return data;
    }

    return createOptimisticUpdaterWithData(
      data.company.preferences.sendBumpStrategy,
      shouldAutoAssignChannel(channel),
      data.userId,
      data.preferences.shouldSubscribeOnReply,
      message.conversationId,
    )(data);
  };

  return optimisticUpdater;
}

async function sendMessageAsync(
  dispatch: Dispatch,
  getState: StateGetter,
  messageUid: string,
  sendOptions: SendMessageOptionsModel,
  originalOptions: SendMessageOptions,
  offlineTask: AppTask | undefined,
): Promise<DataGroupModel | undefined> {
  // If options.setVersion was not provided, add our own so we have conflict detection between the save and send.
  const options = maybeAddMessageVersionSetterToOptions(originalOptions);

  // We have to use the legacy task queue, because saveMessageAsyncAction uses it and so it may be holding on to
  // results from API calls.
  const messageQueue = findMessageQueue(dispatch, getState, messageUid);

  // Schedule saving the message, in case it has unsaved changes.
  const savePromise = dispatch(flushMessageAsyncAction(messageUid, options));

  // Schedule our turn to send the message immediately following the save (if any).
  const messageQueueTurnPromise = messageQueue.waitForTurnAsync();
  // Try/catch to ensure we balance our waitForTurnAsync() call.
  try {
    // Wait for the save (if any) and propagate any errors thrown during the save.
    // TODO: Reduce duplication with trySendMessageOnlineOrOfflineAsync and sendMessageOnlineOrOfflineAsync.
    try {
      await savePromise;
    } catch (error) {
      const isOfflineError = error instanceof OfflineError;
      if (!isOfflineError || !offlineTask) {
        throw error;
      }

      // Try to send offline. This will replace the optimistic update.
      await dispatch(scheduleOfflineTaskAsyncAction(offlineTask, error));
      messageQueueTurnPromise.then(async () => {
        messageQueue.provideResultAsync({});
      });

      // Show the undo banner.
      maybeShowUndoBanner(
        dispatch,
        getState,
        messageUid,
        MessageUndoBannerTypesEnum.OFFLINE,
        Date.now() + sendOfflineUndoDelay,
      );

      return undefined;
    }

    // Wait for our turn to send the message.
    await messageQueueTurnPromise;

    // Get the conversation lock by getting the conversation id from the message.
    const messageFromQueue = messageQueue.getResult();
    if (!messageFromQueue || messageFromQueue.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
      throw new Error('The specified message could not be found.');
    }

    // Wait for our turn to update the conversation.
    // Warning: for deadlock avoidance, we should acquire the message lock before the conversation lock!
    return await findConversationLock(messageFromQueue.conversationId).lockAsync(async () => {
      const state = getState();
      const optimisticData = getOptimisticData(state);
      const userId = getUserId(state);
      const company = getCompany(state);

      const message = messageQueue.getResult();
      if (!message || message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
        throw new Error('The specified message could not be found.');
      }

      if (isDirtyDraftMessage(message)) {
        throw new Error('Failed to save message before send.');
      }

      const optimisticChannel = optimisticData.channels.byId[message.fromChannelId];
      if (!optimisticChannel) {
        throw new Error('The specified send channel could not be found.');
      }

      const optimisticConversation = optimisticData.conversations.byId[message.conversationId];

      // Validate that this message can be sent.
      if (!isDraftValid(message, optimisticChannel, company)) {
        leaveBreadcrumb('[validations-errors]', {
          validationErrors: findDraftValidationErrors(message, optimisticChannel, company),
        });
        throw new Error('This draft is not in a valid state.');
      }

      const sendAt = findSendAt(optimisticChannel, sendOptions);
      const resultDataGroup = await trySendMessageOnlineOrOfflineAsync(
        dispatch,
        getState,
        userId,
        optimisticChannel,
        optimisticConversation,
        message,
        sendOptions,
        sendAt,
        options,
        offlineTask,
      );

      if (!resultDataGroup) {
        messageQueue.provideResultAsync({});
        return undefined;
      }

      messageQueue.provideResultAsync(pick(resultDataGroup, 'messages'));

      resetConversationTimer(message.conversationId);

      return omit(resultDataGroup, 'messages');
    });
  } catch (error) {
    messageQueueTurnPromise.then(() => {
      messageQueue.reportError();
    });

    throw error;
  }
}

async function trySendMessageOnlineOrOfflineAsync(
  dispatch: Dispatch,
  getState: StateGetter,
  userId: number,
  channel: ChannelModel,
  conversation: ConversationModel | undefined,
  message: DraftMessageModel,
  sendOptions: SendMessageOptionsModel,
  sendAt: number,
  options: SendMessageOptions,
  offlineTask: AppTask | undefined,
): Promise<DataGroupModel | undefined> {
  try {
    const result = await sendMessageOnlineOrOfflineAsync(
      dispatch,
      userId,
      channel,
      conversation,
      message,
      sendOptions,
      options,
      offlineTask,
    );

    const sendIn = sendAt - Date.now();
    const canUndo = !sendOptions.sendLaterAt && sendIn > 1000;
    if (canUndo) {
      const undoBannerType = result ? MessageUndoBannerTypesEnum.ONLINE : MessageUndoBannerTypesEnum.OFFLINE;
      maybeShowUndoBanner(dispatch, getState, message.uid, undoBannerType, sendAt);
    }

    return result;
  } catch (error) {
    // Report non-HTTP errors to Bugsnag.
    if (!(error instanceof HttpError)) {
      reportToBugsnag(error);
    }

    if (error instanceof HttpResponseError && error.status === 409) {
      const failedMessage = updateMessage(getState(), message, {
        error: {
          source: MessageErrorSourcesEnum.CLIENT,
          type: MessageErrorsEnum.VERSION_CONFLICT,
        },
      });

      return {messages: [failedMessage]};
    }

    const failedMessage = updateMessage(getState(), message, {
      status: MessageStatusesEnum.DRAFT_FAILED,
      sendOptions,
      sentAt: sendAt,
    });

    return {messages: [failedMessage]};
  }
}

async function sendMessageOnlineOrOfflineAsync(
  dispatch: Dispatch,
  userId: number,
  channel: ChannelModel,
  conversation: ConversationModel | undefined,
  message: DraftMessageModel,
  sendOptions: SendMessageOptionsModel,
  options: SendMessageOptions,
  offlineTask: AppTask | undefined,
): Promise<DataGroupModel | undefined> {
  try {
    // Try to send online.
    return await sendMessageOnlineAsync(
      userId,
      channel,
      conversation,
      {
        ...message,
        version: options.getVersion(),
      },
      sendOptions,
    );
  } catch (error) {
    // Can we send offline instead?
    const isOfflineError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
    if (!isOfflineError || !offlineTask) {
      throw error;
    }

    // Try to send offline. This will replace the optimistic update.
    await dispatch(scheduleOfflineTaskAsyncAction(offlineTask, error));

    return undefined;
  }
}

async function sendMessageOnlineAsync(
  userId: number,
  channel: ChannelModel,
  conversation: ConversationModel | undefined,
  message: DraftMessageModel,
  sendOptions: SendMessageOptionsModel,
): Promise<DataGroupModel> {
  const {cellsContext, sendAction, remindAt} = sendOptions;
  const shouldArchive = sendAction === SendActionsEnum.SEND_AND_ARCHIVE;

  const {shouldUpdateTracker, shouldUpdateGlobal} = computeSendMessageContext(
    userId,
    channel,
    conversation,
    cellsContext,
  );
  const tracker = shouldUpdateTracker ? buildServerTracker(userId, sendOptions) : undefined;

  // Reserve the handle time increment to be saved.
  const {handleTimeIncrement, cancelUnsavedHandleTime} = reserveHandleTimeToSave(message);
  if (handleTimeIncrement > 3600) {
    handleTimeLogger.info('handleTimeIncrement when sending', {handleTimeIncrement});
  }

  // Perform the request.
  const body = {
    type: 'outbound',
    message: {
      uid: message.uid,
      version: message.version,
    },
    meta: {
      archive: shouldUpdateGlobal ? shouldArchive : undefined,
      delay: findSendDelay(channel, sendOptions),
      reminder: shouldUpdateGlobal ? remindAt : undefined,
      tag_ids: sendOptions.tagIds,
      trackers: tracker ? [tracker] : undefined,
      handle_time_increment: handleTimeIncrement,
    },
  };

  try {
    return await sendMessageApiAsync(message, body);
  } catch (error) {
    cancelUnsavedHandleTime();

    throw error;
  }
}

function computeSendMessageContext(
  userId: number,
  channel: ChannelModel,
  conversation: ConversationModel | undefined,
  cellsContext: CellsContext,
) {
  // Determine our context.
  const willBeAssignee =
    !isUndefined(conversation) &&
    shouldAutoAssignChannel(channel) &&
    shouldAutoAssignConversation(conversation);
  const isAssignee = !isUndefined(conversation) && conversation.assigneeId === userId;
  const isTeamContext = !isUndefined(conversation) && isConversationInTeamContext(cellsContext, conversation);

  // Determine if we should global snooze/archive.
  const shouldUpdateGlobal = willBeAssignee || isAssignee || isTeamContext;

  // Determine if we should tracker snooze/archive.
  const shouldUpdateTracker = willBeAssignee || isAssignee || !isTeamContext;

  return {shouldUpdateTracker, shouldUpdateGlobal};
}

function updateMessage(state: AppState, message: DraftMessageModel, overrides: Partial<DraftMessageModel>) {
  return {
    ...message,
    ...overrides,
    ...buildOptimisticContent(state, message),
    isSaving: true,
  };
}

type DraftMessageRenderedContent = Required<Pick<DraftMessageModel, 'lightBody' | 'body' | 'blurb'>>;

function buildOptimisticContent(state: AppState, message: DraftMessageModel): DraftMessageRenderedContent {
  return {
    lightBody: buildOptimisticLightBody(state, message),
    body: buildOptimisticBody(state, message),
    blurb: buildOptimisticBlurb(message),
  };
}

/** Make a temporary light body for use before we get the server's response. */
function buildOptimisticLightBody(state: AppState, message: DraftMessageModel) {
  const messageContent = message.html || textToHtml(message.text);
  const signatureHtml = buildSignatureHtml(state, message);

  // If there's no signature, return as-is.
  if (!message.isSignatureIncluded || !signatureHtml) {
    return messageContent;
  }

  // Otherwise, add the signature.
  return `
    ${messageContent}
    <br>
    <div class="front-signature">
      ${signatureHtml || ''}
    </div>
  `;
}

/** Make a temporary body for use before we get the server's response. */
function buildOptimisticBody(state: AppState, message: DraftMessageModel) {
  const lightBody = buildOptimisticLightBody(state, message);
  if (message.type !== MessageTypesEnum.EMAIL || !message.isQuoteIncluded) {
    return lightBody;
  }

  return `
    ${lightBody}
    <br>
    <blockquote type="cite" class="front-blockquote">
      ${message.quoteHtml || ''}
    </blockquote>
  `;
}

/** Make a temporary blurb for use before we get the server's response. */
function buildOptimisticBlurb(message: DraftMessageModel) {
  // Convert the message content to text to use as blurb.
  const messageContent = message.html || textToHtml(message.text);
  return htmlToText(messageContent);
}

function sendMessageInConversation(
  bumpStrategy: BumpStrategiesEnum,
  shouldAutoAssign: boolean,
  userId: number,
  shouldAutoSubscribe: boolean,
  sendOptions: SendMessageOptionsModel,
) {
  const {cellsContext, sendAction, remindAt} = sendOptions;
  const shouldArchive = sendAction === SendActionsEnum.SEND_AND_ARCHIVE;

  return buildConversationUpdater(
    // If the conversation is not assigned, maybe assign to ourselves.
    // TODO: Should this be the userId?
    shouldAutoAssign && autoAssignConversation(cellsContext.teammateId),
    // If needed, become a subscriber of this conversation.
    shouldAutoSubscribe && autoSubscribeToConversation(userId),
    // Maybe add a reminder to the conversation.
    remindAt && setConversationReminder(cellsContext, remindAt),
    // If needed, bump the conversation.
    bumpConversationOnSend(bumpStrategy, BumpStrategiesEnum.MESSAGE, cellsContext.teammateId),
    (conversation) => {
      // If we don't need to archive in the first place, nothing to do.
      if (!shouldArchive) {
        return conversation;
      }

      // If the conversation is already archived, nothing to do.
      const status = findConversationStatus(cellsContext, conversation);
      if (status === ConversationStatusesEnum.ARCHIVED) {
        return conversation;
      }

      // Bump the conversation for the tracker and update the status.
      return buildConversationUpdater(
        bumpConversationTracker(cellsContext.teammateId),
        setConversationStatus(cellsContext, ConversationStatusesEnum.ARCHIVED),
      )(conversation);
    },
  );
}

function buildServerTracker(userId: number, options: SendMessageOptionsModel) {
  const {sendAction, remindAt} = options;
  const isSendAndArchive = sendAction === SendActionsEnum.SEND_AND_ARCHIVE;

  return {
    teammate_id: userId,
    reminder: remindAt,
    status: isSendAndArchive ? ConversationTrackerStatusesEnum.ARCHIVED : undefined,
  };
}

/*
 * Undo helpers.
 */

function findUndoSendDelay(channel: ChannelModel | undefined) {
  if (!channel) {
    return undefined;
  }

  const channelPreferences = channel.preferences;
  if (!channelPreferences || !channelPreferences.canUndoSend || !channelPreferences.undoSendPeriod) {
    return undefined;
  }

  return channelPreferences.undoSendPeriod;
}

function findSendAt(channel: ChannelModel | undefined, options: SendMessageOptionsModel) {
  if (options.sendLaterAt) {
    return options.sendLaterAt;
  }

  const undoSendDelay = findUndoSendDelay(channel);
  if (undoSendDelay) {
    return Date.now() + undoSendDelay * 1000;
  }

  return Date.now();
}

function findSendDelay(channel: ChannelModel, options: SendMessageOptionsModel) {
  if (options.sendLaterAt) {
    return Math.round((options.sendLaterAt - Date.now()) / 1000);
  }

  const undoSendDelay = findUndoSendDelay(channel);
  if (undoSendDelay) {
    return undoSendDelay + 5;
  }

  return undefined;
}

function maybeShowUndoBanner(
  dispatch: Dispatch,
  getState: StateGetter,
  messageUid: string,
  undoType: MessageUndoBannerTypesEnum,
  expiresAt: number,
) {
  // Find the panel to display the undo action in.
  // TODO: Find a better way to get a panel ID in here.
  const appPanelId = getOptionalFirstAppPanelId(getState());

  // We may not have an app panel in tests.
  // TODO: Refactor action creators to separate data concerns from UI/panel concerns.
  if (!appPanelId) {
    return;
  }

  // Dispatch the undo banner.
  dispatch(showMessageUndoBannerAction(appPanelId, messageUid, undoType, expiresAt));
}

function maybeDispatchConversationSwitchingActions(
  dispatch: AppThunkDispatch,
  getState: () => AppState,
  sendOptions: SendMessageOptionsModel,
  messageUid: string,
) {
  if (sendOptions.sendAction === SendActionsEnum.SEND_AND_ARCHIVE) {
    dispatch(clearHiddenConversationIdAction());
    return;
  }

  const {channel, conversation} = findDraftMessageData(getState().originalData, messageUid);

  // If we don't have a channel or a conversation, nothing we can do.
  if (!channel || !conversation) {
    return;
  }

  const willChannelAutoAssign = shouldAutoAssignChannel(channel);

  // If the channel won't auto-assign, nothing to do.
  if (!willChannelAutoAssign) {
    return;
  }

  const optimisticConversation = getConversations(getState())[conversation.id];

  if (!optimisticConversation) {
    return;
  }

  dispatch(maybeHideConversationAction(conversation, optimisticConversation));
}
